@if (model) {
  <form [formGroup]="model">
    <div class="d-flex justify-content-between align-content-center">
      <div>
        <h4>
          {{ form.header }}

          @if (isLoading) {
            <div
              style="
                display: inline-block;
                margin-right: 10px;
                position: relative;
                top: 3px;
              "
            >
              <p-progressSpinner
                [style]="{ width: '20px', height: '20px' }"
                strokeWidth="6"
                animationDuration="1.5s"
              >
              </p-progressSpinner>
            </div>
          }
        </h4>
      </div>

      <div class="form-header-actions">
        @for (button of getHeaderButtons(); track $index) {
          <button
            [class]="button.class ?? 'btn btn-primary'"
            [style]="button.style"
            (click)="executeConfiguredButton(button)"
            type="button"
          >
            @if (button.icon) {
              <i [class]="button.icon" aria-hidden="true"></i>
            }
            <span>{{ button.label }}</span>
          </button>
        }

        @if (form.uploadButton.isEnabled) {
          <app-reactive-upload-images
            [formModel]="model.getRawValue()"
            [buttonLabel]="form.uploadButton.label"
            [imageFolder]="form.name"
            [formType]="form.type"
            [imageDisplay]="'inline'"
            [imageName]="'imagen'"
            (sendImageName)="updateImageModel($event, 'imagen')"
          >
          </app-reactive-upload-images>
        }
      </div>
    </div>

    <div class="row flex-wrap">
      <!-- FORM FIELDS -->
      @for (section of form.sections; track section) {
        @if (section.isVisible) {
          <div class="row g-4 align-items-stretch">
            <!-- Left: fields -->
            <div class="col-12 col-md-{{ section.width }} mb-3">
              <div class="row align-items-center">
                <div class="col-12 col-md-12">
                  <h4>{{ section.name }}</h4>
                </div>

                @for (button of section.buttons; track $index) {
                  <div class="col-auto">
                    <button
                      *ngIf="button.isVisible"
                      [class]="button.class"
                      [style]="button.style"
                      (click)="button?.function()"
                      type="button"
                    >
                      <i [class]="button.icon" aria-hidden="true"></i>
                      <span>{{ button.label }}</span>
                    </button>
                  </div>
                }
              </div>

              <div class="row g-3 align-items-end">
                @for (field of section.fields; track field) {
                  @if (field.conditions.isVisible) {
                    <div class="col-12 col-sm-{{ field.width }} mb-3">
                      <!-- ✅ Progressive rendering: show field only when ready -->
                      @if (isFieldReady(field)) {
                        <app-field
                          [form]="form"
                          [field]="field"
                          [model]="model"
                          [undoEnabled]="undoEnabled"
                          [originalModel]="originalModel"
                          [filterInputParcelas]="filterInputParcelas"
                          [debug]="debug"
                        >
                        </app-field>
                      } @else {
                        <!-- ✅ Placeholder while dropdown/options are still loading -->
                        <div class="w-100">
                          <div class="field-label mb-1">
                            <span class="fw-semibold">
                              {{ field.label ?? field.name }}
                            </span>

                            @if (field.conditions.isRequired) {
                              <span class="required-asterisk ms-1">*</span>
                            }
                          </div>

                          <!-- If you have PrimeNG Skeleton available -->
                          <p-skeleton height="2.5rem" styleClass="w-100"></p-skeleton>

                          @if (getPendingDisplay(field)) {
                            <small class="text-muted d-block mt-1">
                              Saved: {{ getPendingDisplay(field) }}
                            </small>
                          }
                        </div>
                      }
                    </div>
                  }
                }
              </div>
            </div>

            <!-- Right: tables -->
            <div class="col-12 col-md w-100">
              @if (section.tables !== null && form.type < 3) {
                <div class="d-flex flex-column gap-4 w-100">
                  @for (table of section.tables; track table) {
                    @if (table.title) {
                      <h2 class="fert-table-title">{{ table.title }}</h2>
                    }

                    <table
                      class="table-style responsive-stacked"
                      [class.no-index]="table.showIndex === false"
                    >
                      @for (row of table.data; track row; let rowNumber = $index) {
                        <tr
                          [style.background]="table.style?.[rowNumber]"
                          [style.color]="table.style?.[rowNumber] === '#222222' ? 'white' : 'black'"
                          [attr.data-row]="rowNumber"
                        >
                          @for (column of row; track column; let colNumber = $index) {
                            @if (rowNumber === 0) {
                              @if (colNumber === 0 && table.showIndex !== false) {
                                <th class="idx-cell" style="width: 15px; border: none"></th>
                              }
                              <th>{{ column }}</th>
                            } @else {
                              @if (colNumber === 0 && table.showIndex !== false) {
                                <td
                                  class="idx-cell"
                                  style="
                                    border: none;
                                    background: #f8f9fa !important;
                                    color: black !important;
                                  "
                                >
                                  {{ rowNumber }}
                                </td>
                              }

                              <td
                                [attr.data-label]="table.data?.[0]?.[colNumber] ?? ''"
                                class="data-cell"
                              >
                                {{ column }}
                              </td>
                            }
                          }
                        </tr>
                      }
                    </table>
                  }
                </div>
              }
            </div>
          </div>
        }
      }

      <!-- TABLE -->
      @if (form.table !== null) {
        <div class="d-flex justify-content-center">
          <table class="table-style">
            @for (row of form.table.data; track row; let rowNumber = $index) {
              <tr
                [style.background]="form.table.style?.[rowNumber]"
                [style.color]="form.table.style?.[rowNumber] === '#222222' ? 'white' : 'black'"
              >
                @for (column of row; track column; let colNumber = $index) {
                  @if (rowNumber === 0) {
                    @if (colNumber === 0) {
                      <th style="width: 15px; border: none"></th>
                    }

                    <th>{{ column }}</th>
                  } @else {
                    @if (colNumber === 0) {
                      <td
                        style="
                          border: none;
                          background: #f8f9fa !important;
                          color: black !important;
                        "
                      >
                        {{ rowNumber }}
                      </td>
                    }

                    <td>{{ column }}</td>
                  }
                }
              </tr>
            }
          </table>
        </div>
      }

      <br />

      <!-- SIGNATURE -->
      @if (form.signed) {
        @if (model.get('signature_img')?.value) {
          <div
            class="distribution_camera_signature"
            style="display: flex; gap: 50px; align-items: center"
          >
            <div>
              <div style="display: flex; flex-direction: column">
                <p>Firma</p>

                @if (!model.get('signature_img')?.value.includes('base64')) {
                  <img
                    alt="Firma"
                    [src]="
                      serverUrl +
                      'images/fitosanitarios/' +
                      model.get('signature_img')?.value
                    "
                    class="info-firma-img"
                  />
                } @else {
                  <img
                    alt="Firma"
                    [src]="model.get('signature_img')?.value"
                    class="info-firma-img"
                  />
                }
              </div>
            </div>
          </div>
        }

        @if (form.type < 3) {
          <app-signature
            (sendFile)="getSignature($event)"
            (sendTempImg)="updateFirma($event)"
          >
          </app-signature>
        }
      }

      <!-- ACTION BUTTONS -->
      @if (form.type <= 4 && showActionsButtons) {
        <div class="d-flex justify-content-center mt-4">
          <div>
            <button
              class="btn btn-dark mx-2"
              (click)="form.type < 3 ? returnToMainView() : closeDialog()"
              type="button"
            >
              {{ form.type < 3 ? 'Volver' : 'Cerrar' }}
            </button>
          </div>

          @if (form.type <= 3) {
            <app-reactive-request-button
              [form]="form"
              [model]="model"
              [recordHasAssociatedData]="recordHasAssociatedData"
              [request]="request"
              [getRequest]="getRequest"
              [lockOnSuccess]="true"
              (goBack)="returnToMainView()"
              (afterSend)="executePostSubmissionLogic($event)"
            >
            </app-reactive-request-button>
          }
        </div>
      }
    </div>
  </form>
}
